import { Box, Tab, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { FC, useEffect, useMemo } from 'react';
import { ReactComponent as LanguagesIcon } from '../../../assets/icons/languages.svg';
import { FaUserCircle } from 'react-icons/fa';
import GeneralInfo from './GeneralInfo';
import IconWrapper from './IconWrapper';
import Languages from './Languages';
import Personas from './Personas';
import { FaInfoCircle } from 'react-icons/fa';
import { important, Nullable } from 'common-types';
import { ApplicationInfo } from '../../applications-types';
import AppFlowsApps from '../ApplicationAppFlow/AppFlowsApps';
import SkeletonLoader from '../../../executive-summary/components/SkeletonLoader';
import IssuesGraph from '../../../executive-summary/components/IssuesGraph';
import { realFakeApp, statisticsExist } from '../../utils/application-utils';
import { FilterIssueType } from '../../../issues/common/types/issues-types';
import ApplicationStatistics from './ApplicationStatistics';
import useOxNamedTabs from '../../../elements/OxTabs/hooks/useOxNamedTabs';
import OxTabs from '../../../elements/OxTabs/OxTabs';
import GitActivityContent from './GitActivityContent';
import { DiGit } from 'react-icons/di';
import { BiStats, BiTrendingUp } from 'react-icons/bi';
import { AiOutlineUnorderedList } from 'react-icons/ai';
import { BsChevronDoubleRight } from 'react-icons/bs';

const ApplicationCard: FC<React.PropsWithChildren<ApplicationCardProps>> = ({
  app,
  applicationIssuesScore,
  applicationStatisticas,
}) => {
  const { classes } = useStyles();

  const statsFiltered = statisticsExist(applicationStatisticas);

  const tabs: { title: string; component: JSX.Element; icon?: JSX.Element }[] =
    useMemo(() => {
      const t: { title: string; component: JSX.Element; icon?: JSX.Element }[] =
        [];
      t.push({
        title: 'Application Flow',
        icon: <BsChevronDoubleRight size={10} />,
        component: (
          <>
            {(app.flows || realFakeApp(app.type)) && (
              <div className={classes.flow}>
                <AppFlowsApps flow={app.flows} iconSize={35} />
              </div>
            )}
          </>
        ),
      });
      if (!app.fakeApp) {
        t.push({
          title: 'General Info',
          icon: <AiOutlineUnorderedList />,
          component: (
            <div key='General Info' className={classes.footer}>
              <div>
                <div className={classes.title}>
                  <IconWrapper icon={FaInfoCircle} />
                  <Typography variant='body1' component='span'>
                    General Info
                  </Typography>
                </div>
                <Box mt={1}>
                  <GeneralInfo
                    developers={app.userCount}
                    files={app.filesCount}
                    repoSize={app.size}
                    lastCodeChangedString={
                      app.lastCodeChangeDate && app.lastCodeChangeDate
                    }
                    lastCodeChanged={
                      app.lastCodeChange
                        ? new Date(Number(app.lastCodeChange))
                        : undefined
                    }
                    createdAt={app.createdAt}
                    accessLevel={app.publicVisibility ? 'Public' : 'Private'}
                    monoRepoParent={app.monoRepoParent}
                    isMonoRepoChild={app.isMonoRepoChild}
                  />
                </Box>
              </div>

              <div className={classes.languages}>
                <div className={classes.title}>
                  <IconWrapper icon={LanguagesIcon} />
                  <Typography variant='body1' component='span'>
                    Languages
                  </Typography>
                </div>
                <Box mt={1}>
                  <Languages items={app.languages} />
                </Box>
              </div>

              <div>
                <div className={classes.title}>
                  <IconWrapper icon={FaUserCircle} />
                  <Typography variant='body1' component='span'>
                    Personas
                  </Typography>
                </div>
                <Box mt={1}>
                  <Personas app={app} />
                </Box>
              </div>
            </div>
          ),
        });
        t.push({
          title: 'Git Activity',
          icon: <DiGit size={16} />,
          component: (
            <GitActivityContent
              key='Git Activity'
              pushEvents={app.pushCount}
              pullRequests={app.pullCount}
              Tags={app.tagsCount}
              YAMLs={app.yamlsCount}
              commits={app.commitCount}
              branches={app.branchesCount}
              analyzedBranches={app.branch}
              watchers={app.watchersCount}
              downloads={app.hasDownloads ? 'Yes' : 'No'}
              forks={0}
            />
          ),
        });
        applicationIssuesScore &&
          t.push({
            title: 'Issue Trends',
            icon: <BiTrendingUp />,
            component: (
              <>
                {Object.keys(applicationIssuesScore.data).length > 0 && (
                  <div key='Issues Trends' className={classes.trend}>
                    <SkeletonLoader loading={!applicationIssuesScore}>
                      <IssuesGraph {...applicationIssuesScore} height='180px' />
                    </SkeletonLoader>
                  </div>
                )}
              </>
            ),
          });

        statsFiltered &&
          Object.keys(statsFiltered).length > 0 &&
          t.push({
            title: 'Issue Statistics',
            icon: <BiStats />,
            component: (
              <ApplicationStatistics
                applicationStatisticas={statsFiltered}
                applicationSelected={app.appName}
              />
            ),
          });
      }
      return t;
    }, [
      app,
      applicationIssuesScore,
      classes.flow,
      classes.footer,
      classes.languages,
      classes.title,
      classes.trend,
      statsFiltered,
    ]);

  const { selectedKey, handleTabChange } = useOxNamedTabs('Application Flow');

  useEffect(() => {
    if (!tabs.some(({ title }) => selectedKey === title)) {
      handleTabChange(null, 'Application Flow');
    }
  }, [handleTabChange, selectedKey, tabs]);

  return (
    <>
      <OxTabs
        className={classes.tabs}
        selectedTab={selectedKey}
        onChange={handleTabChange}>
        {tabs.map(({ title, icon }) => (
          <Tab
            iconPosition='start'
            icon={icon}
            key={title}
            value={title}
            label={title}
            sx={{ fontSize: 12 }}
          />
        ))}
      </OxTabs>
      <div style={{ padding: 16 }}>
        {tabs.map(
          ({ component, title }, i) => selectedKey === title && component,
        )}
      </div>
    </>
  );
};
const useStyles = makeStyles()((theme: Theme) => ({
  tabs: {
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.modal,
    backgroundColor: theme.palette.background.paper,
    height: 60,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  applicationCard: {
    paddingBlock: theme.spacing(2),
    paddingInline: theme.spacing(3),
  },
  languages: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingInline: theme.spacing(4),
  },
  flow: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-evenly',
    marginBlockStart: '5%',
  },
  footer: {
    paddingBlock: theme.spacing(3),
    display: 'grid',
    gridTemplateColumns: 'repeat(4,auto)',
    gap: 30,
  },
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    marginBlockEnd: theme.spacing(2),
    paddingInlineStart: 1,
    '& span': {
      fontWeight: important('600'),
    },
    '& svg': {
      color: theme.palette.primary.light,
    },
  },
  spaceTrend: {
    paddingBlockStart: theme.spacing(3),
  },
  trend: {
    padding: theme.spacing(3),
    height: '90%',
  },
}));

export interface ApplicationCardProps {
  app: ApplicationInfo;
  applicationIssuesScore?: {
    data: { [key: string]: number[] };
    labels: number[];
  };
  applicationStatisticas: Nullable<FilterIssueType>;
}

export default ApplicationCard;
